import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useConvexAuth, useQuery } from "convex/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "@convex/_generated/api";
import { ClawbertMark } from "@/components/home/ClawbertMark";
import { CodeInput } from "@/components/signup/CodeInput";
import { Spinner } from "@/components/ui/Spinner";
import { useAuth } from "@/hooks/useAuth";
import { useForceLightMode } from "@/hooks/useTheme";

export const Route = createFileRoute("/login")({
  component: LoginRoute,
  validateSearch: (search: Record<string, unknown>) => ({
    pendingCode: (search.pendingCode as string) || undefined,
  }),
});

const ERROR_MESSAGES: Record<string, string> = {
  INVALID_CODE: "That code doesn't look right. Check and try again.",
  CODE_REVOKED: "This code has been revoked.",
  CODE_EXPIRED: "This code has expired.",
  CODE_MAXED: "This code has reached its usage limit.",
  SIGNUPS_DISABLED: "Signups are currently closed.",
};

const primaryButton =
  "w-full rounded-full bg-home-yellow px-6 py-3.5 font-body text-[15px] font-bold text-home-ink shadow-[0_10px_28px_-12px_rgba(33,31,28,0.4)] transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-[0_16px_34px_-12px_rgba(33,31,28,0.45)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-[0_10px_28px_-12px_rgba(33,31,28,0.4)]";

const inkButton =
  "w-full rounded-full bg-home-ink px-6 py-3.5 font-body text-[15px] font-bold text-home-cream transition-all duration-300 ease-out hover:-translate-y-0.5 hover:bg-home-ink/85";

function LoginRoute() {
  useForceLightMode();
  const { signIn } = useAuth();
  const { isAuthenticated } = useConvexAuth();
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const policy = useQuery(api.signupCodes.checkSignupPolicy);

  const [code, setCode] = useState("");

  const validation = useQuery(
    api.signupCodes.validateSignupCode,
    code.length === 5 ? { code } : "skip",
  );

  const isValid = validation?.accepted === true;
  const validationError = validation?.accepted === false ? validation.reason : null;

  useEffect(() => {
    if (isAuthenticated) {
      void navigate({ to: "/chat", search: { template: undefined } });
    }
  }, [isAuthenticated, navigate]);

  if (policy === undefined) {
    return <Spinner />;
  }

  const isOpen = policy.mode === "OPEN";
  const isDisabled = policy.mode === "DISABLED";
  const showCodeEntry = !isOpen && !isDisabled;

  function handleSignIn() {
    // Encode pending code in redirectTo so it survives the OAuth round-trip (INV-12)
    const redirectTo = code
      ? `/chat?pendingCode=${encodeURIComponent(code)}`
      : "/chat";
    void signIn("google", { redirectTo });
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-home-cream p-6">
      {/* Soft candy wash behind the card */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-[-140px] left-1/2 h-[440px] w-[560px] -translate-x-1/2 rounded-[50%] bg-home-butter opacity-40 blur-3xl"
      />

      <div className="relative w-full max-w-md rounded-[32px] bg-white p-9 text-center shadow-[0_28px_60px_-28px_rgba(33,31,28,0.4)] md:p-10">
        <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-home-sky">
          <ClawbertMark pose="wave" className="h-20 w-20" />
        </div>

        <h1 className="font-display text-3xl font-semibold tracking-tight text-home-ink">
          Welcome to Clawcraft
        </h1>
        <p className="mx-auto mt-2 max-w-xs font-body text-[15px] leading-relaxed text-home-ink/60">
          {isDisabled
            ? "Signups are currently closed. Check back later."
            : "Sign in. Clawbert will take it from there."}
        </p>

        <div className="mt-8">
          {showCodeEntry ? (
            <CodeEntryStep
              code={code}
              onCodeChange={(c) => setCode(c)}
              isValid={isValid}
              validationError={validationError}
              onContinue={handleSignIn}
              onSkipToSignIn={handleSignIn}
            />
          ) : (
            <button
              type="button"
              onClick={handleSignIn}
              disabled={isDisabled}
              className={primaryButton}
            >
              {t("actions.signInWithGoogle")}
            </button>
          )}
        </div>

        <p className="mt-8 font-body text-xs text-home-ink/40">
          By continuing you agree to our{" "}
          <Link to="/terms" className="underline underline-offset-2 hover:text-home-ink/70">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link to="/privacy" className="underline underline-offset-2 hover:text-home-ink/70">
            Privacy Policy
          </Link>
          .
        </p>
      </div>
    </div>
  );
}

function CodeEntryStep({
  code,
  onCodeChange,
  isValid,
  validationError,
  onContinue,
  onSkipToSignIn,
}: {
  code: string;
  onCodeChange: (code: string) => void;
  isValid: boolean;
  validationError: string | null | undefined;
  onContinue: () => void;
  onSkipToSignIn: () => void;
}) {
  const displayError = validationError ? ERROR_MESSAGES[validationError] ?? validationError : null;

  return (
    <div className="space-y-6">
      <div>
        <p className="mb-3 font-body text-sm font-medium text-home-ink/70">
          Enter your access code
        </p>
        <CodeInput value={code} onChange={onCodeChange} error={!!displayError} />
      </div>

      {displayError && (
        <p className="font-body text-sm text-home-oxblood">{displayError}</p>
      )}

      <button type="button" onClick={onContinue} disabled={!isValid} className={primaryButton}>
        Continue
      </button>

      <div className="flex items-center gap-4">
        <div className="h-px flex-1 bg-home-ink/10" />
        <span className="font-body text-xs font-medium tracking-wide text-home-ink/35 uppercase">
          or
        </span>
        <div className="h-px flex-1 bg-home-ink/10" />
      </div>

      <button type="button" onClick={onSkipToSignIn} className={inkButton}>
        Log in with Google
      </button>
    </div>
  );
}
